/* filmControl — the play_film client tool.

   The shipped films are already on the public pages (PageFilm on
   /platform, /app-kit, /gate, /pricing; the module short in the hero of
   /platform/<module>). This finds the one the model named, brings it into
   view, plays it with its sound, and tells the live voice session when it
   ends (a FILM ENDED system item through the same nv:voice-context event
   the Lab uses) so NoVA picks the pitch back up. A person speaking pauses
   it (nv:voice-speech-started from useVoiceSession's barge-in). [[nova-pitch]] */
import { PITCH_FILMS } from '../data/pitch.js';

const POLL_MS = 150;
const POLL_MAX = 24; // ~3.6 s — the page may still be mounting after a navigation

function findVideo(prefix, film) {
  if (film.module) return document.querySelector('.cs-mod-film video');
  return document.querySelector(`#${prefix}-film video`);
}

function waitForVideo(prefix, film) {
  return new Promise((resolve) => {
    let n = 0;
    const tick = () => {
      const v = findVideo(prefix, film);
      if (v || n++ >= POLL_MAX) return resolve(v || null);
      setTimeout(tick, POLL_MS);
    };
    tick();
  });
}

let current = null; // { video, cleanup }
function detach() {
  if (current?.cleanup) current.cleanup();
  current = null;
}

// A hidden user-role turn, not a system item: after the terse "Here it is."
// a system note came back as "Here it is." again (the model echoes its
// last turn); a user turn is always answered on its own terms.
function announceEnded(title) {
  window.dispatchEvent(new CustomEvent('nv:voice-context', {
    detail: { text: `(The film "${title}" just finished.) In one sentence, pick the pitch back up where you were and offer the next part — or ask what I thought.`, respond: true, role: 'user' },
  }));
}

export async function playFilm(args = {}) {
  const prefix = String(args?.prefix || '').trim().toLowerCase();
  const film = PITCH_FILMS[prefix];
  if (!film) return { error: `no film "${prefix}" — one of ${Object.keys(PITCH_FILMS).join(', ')}` };
  const action = args?.action === 'pause' ? 'pause' : 'play';
  const video = await waitForVideo(prefix, film);
  if (!video) return { error: `the "${film.title}" film is not on this page — it lives on ${film.route}; open that page first (navigate_app or the pitch step), then play it` };

  if (action === 'pause') {
    video.pause();
    return { ok: true, paused: film.title, at: Math.round(video.currentTime), note: 'Paused. play_film again resumes from here.' };
  }

  detach();
  const at = Number.isFinite(Number(args?.at)) && Number(args.at) >= 0 ? Number(args.at) : null;
  const holder = video.closest('figure, .cs-mod-film') || video;
  try { holder.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { holder.scrollIntoView(); }

  // Module shorts: the page's own "Sound on" button restarts the short from
  // the top with the voice track and returns it to the muted loop at the
  // end — press it so React's state agrees with what is playing.
  if (film.module) {
    const btn = holder.querySelector?.('.cs-mod-film-sound') || document.querySelector('.cs-mod-film-sound');
    const soundOn = btn?.getAttribute('aria-pressed') === 'true';
    if (btn && !soundOn) btn.click();
    else if (video.paused) video.play().catch(() => {});
    if (at != null) video.currentTime = at;
  } else {
    video.loop = false;
    video.muted = false;
    const midway = video.currentTime > 0.5 && video.duration && video.currentTime < video.duration - 1;
    if (at != null) video.currentTime = at;
    else if (!midway || video.ended) video.currentTime = 0;
    try {
      await video.play();
    } catch (_) {
      // Unmuted playback refused (no gesture yet): play muted with the
      // captions showing rather than nothing.
      video.muted = true;
      for (const t of video.textTracks || []) t.mode = 'showing';
      try { await video.play(); } catch (err) { return { error: `could not play the film: ${String(err?.message || err).slice(0, 120)}` }; }
    }
  }

  const onEnded = () => { detach(); announceEnded(film.title); };
  const onSpeech = () => { if (!video.paused) video.pause(); };
  video.addEventListener('ended', onEnded);
  window.addEventListener('nv:voice-speech-started', onSpeech);
  current = {
    video,
    cleanup: () => {
      video.removeEventListener('ended', onEnded);
      window.removeEventListener('nv:voice-speech-started', onSpeech);
    },
  };
  return {
    ok: true,
    playing: film.title,
    seconds: film.seconds,
    ...(at != null ? { from: at } : {}),
    muted: video.muted,
    note: `The film is playing${video.muted ? ' (muted, captions on)' : ' with its sound'}. Reply with a two-word lead-in ("Here it is.") and then wait; a "FILM ENDED" note arrives when it finishes — answer that note out loud. If the person speaks, it pauses by itself and you may answer.`,
  };
}

export function stopFilm() {
  if (current?.video && !current.video.paused) current.video.pause();
  detach();
}
